import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMessageService } from '../../services/common/notification-message.service';
import { CONSTANTS } from '../../Constants/Constants';
import { ProblemDetailsErrorMapperService } from './problem-details-error-mapper.service';
import { IUiErrorMessage } from '../../models/IUiErrorMessage';

@Injectable({ providedIn: 'root' })
export class GlobalApiErrorHandlerService {
	private readonly fallbackMessageKey = 'ERRORS.GENERIC';
	private readonly hardcodedMessage = 'Something went wrong. Please try again.';

	constructor(
		private readonly notificationMessageService: NotificationMessageService,
		private readonly problemDetailsErrorMapperService: ProblemDetailsErrorMapperService,
		private readonly translateService: TranslateService
	) { }

	handle(error: HttpErrorResponse, skipGlobalError = false): void {
		if (skipGlobalError) {
			return;
		}

		const uiError = this.problemDetailsErrorMapperService.map(error);

		this.notificationMessageService.showNotificationMessageDialog(
			CONSTANTS.TYPE_ERROR,
			this.resolveTitle(uiError),
			this.resolveMessage(uiError),
			this.resolveWidth(uiError)
		);
	}

	private resolveWidth(uiError: IUiErrorMessage): string {
		return uiError.errorCode === CONSTANTS.OPEN_ESTIMATE_OUTDATED_LABOR_ERROR_CODE
			? CONSTANTS.OPEN_ESTIMATE_OUTDATED_LABOR_DIALOG_WIDTH
			: CONSTANTS.DEFAULT_DIALOG_WIDTH;
	}

	private resolveTitle(uiError: IUiErrorMessage): string {
		const translated = this.translateService.instant(uiError.titleKey);
		return this.isResolved(translated, uiError.titleKey) ? translated : CONSTANTS.ERROR_TITLE;
	}

	private resolveMessage(uiError: IUiErrorMessage): string {
		const translated = this.translateService.instant(uiError.messageKey, uiError.messageParams);
		if (this.isResolved(translated, uiError.messageKey)) {
			return translated;
		}

		if (uiError.detail && uiError.detail.trim().length > 0) {
			return uiError.detail;
		}

		const generic = this.translateService.instant(this.fallbackMessageKey);
		return this.isResolved(generic, this.fallbackMessageKey) ? generic : this.hardcodedMessage;
	}

	private isResolved(translated: unknown, key: string): boolean {
		return typeof translated === 'string' && translated.length > 0 && translated !== key;
	}
}